import * as React from "react";
import { HoloDot } from "@/components/Holo";
import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio, DesignPackageItem } from "@/services/bedrock/dtos/DesignPackage";
import { PreviewImageState } from "../hooks/usePreviews";
import { SlideState } from "../slideState";
import { SlidePreview } from "./SlidePreview";

interface SlideRailProps {
  items: DesignPackageItem[];
  selectedItemId: number;
  onSelect(itemId: number): void;
  aspectRatio: AspectRatio;
  imageFor(itemId: number): { url: string | null; state: PreviewImageState };
  stateFor(item: DesignPackageItem): SlideState;
}

const NEXT_KEYS = ["ArrowDown", "ArrowRight"];
const PREVIOUS_KEYS = ["ArrowUp", "ArrowLeft"];

/**
 * The slides of a carousel as a filmstrip: down the left of the selected slide on wide screens
 * (scrolling on its own when the slides don't fit), across the top on narrow ones. Arrow keys move
 * between slides.
 */
export const SlideRail: React.FC<SlideRailProps> = ({
  items,
  selectedItemId,
  onSelect,
  aspectRatio,
  imageFor,
  stateFor,
}) => {
  const listRef = React.useRef<HTMLOListElement>(null);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = NEXT_KEYS.includes(event.key) ? 1 : PREVIOUS_KEYS.includes(event.key) ? -1 : 0;
    if (step === 0) return;
    const index = items.findIndex(item => item.id === selectedItemId);
    const next = items[index + step];
    if (!next) return;
    event.preventDefault();
    onSelect(next.id);
    // Keep focus on the newly selected slide so the arrows keep working.
    requestAnimationFrame(() =>
      listRef.current?.querySelector<HTMLButtonElement>(`[data-item-id="${next.id}"] button`)?.focus()
    );
  };

  return (
    <nav
      aria-label="Slides"
      // On wide screens the column overlaps the stage's left border by a pixel and sits above it, so
      // the selected tab can cover that border and open into the stage. The column has no background
      // of its own, so elsewhere the border and the stage's shadow show through.
      className="lg:relative lg:z-10 lg:col-start-1 lg:row-start-1 lg:-mr-px lg:flex lg:min-h-0 lg:flex-col"
    >
      <ol
        ref={listRef}
        onKeyDown={onKeyDown}
        // The list's clipped right edge keeps the tab's own shadow off the stage.
        className="no-scrollbar flex snap-x gap-1 overflow-x-auto p-2 lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:pt-16 lg:pb-3 lg:pr-0 lg:pl-2"
        data-testid="slide-strip"
      >
        {items.map(item => {
          const selected = item.id === selectedItemId;
          const image = imageFor(item.id);
          const state = stateFor(item);
          return (
            <li
              key={item.id}
              data-item-id={item.id}
              aria-current={selected ? "true" : undefined}
              className={cn(
                // Every row keeps a transparent border so selecting one only changes its color, never
                // its width (a border appearing mid-transition flashes dark).
                "group/tab relative flex shrink-0 flex-col items-center gap-1.5 rounded-xl p-2 transition-[background-color,box-shadow] duration-200 lg:border lg:border-r-0 lg:border-transparent lg:pl-4",
                selected
                  ? // On wide screens the selected row runs into the stage like a folder tab, in the stage's
                    // color, open on the side that meets it and lifted with it above the other slides.
                    "bg-slate-50 lg:rounded-r-none lg:border-slate-200 lg:pr-5 lg:shadow-[0_2px_10px_rgb(15_23_42/0.10)]"
                  : // The selected row's extra right padding matches this margin, so the thumbnail
                    // stays put when selected.
                    "hover:bg-slate-50 lg:mr-3"
              )}
              data-testid="slide-rail-item"
            >
              <SlidePreview
                item={item}
                aspectRatio={aspectRatio}
                imageUrl={image.url}
                imageState={image.state}
                state={state}
                selected={selected}
                onClick={() => onSelect(item.id)}
                size="thumb"
                className={cn(
                  "transition-[translate,box-shadow] duration-200 ease-out motion-reduce:transition-none",
                  "[&_img]:transition-transform [&_img]:duration-300 [&_img]:ease-out motion-reduce:[&_img]:transition-none",
                  // On wide screens the tab already shows which slide is selected.
                  selected && "lg:ring-0",
                  !selected &&
                    "group-hover/tab:-translate-y-0.5 group-hover/tab:shadow-md group-hover/tab:[&_img]:scale-105"
                )}
              />
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-xs transition-colors",
                  selected ? "font-semibold text-primary" : "text-slate-500 group-hover/tab:text-slate-800"
                )}
              >
                Slide {item.position}
                {state === "candidate_ready" && (
                  <Tooltip>
                    <Tooltip.Trigger render={<HoloDot label="New version ready" data-testid="thumb-new-version" />} />
                    <Tooltip.Content>New version ready</Tooltip.Content>
                  </Tooltip>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
